<template>
  <div class="pedido-layout">

    <!-- Left: main content column -->
    <div class="pedido-main">

      <!-- Quick actions -->
      <QuickActions
        :activeIds="activeIds"
        :store="store"
        @apply-last-order="handleApplyLastOrder"
      />

      <!-- Comedor strip (hidden in matriz mode) -->
      <ComedorStrip
        :activeIds="activeIds"
        :focused="focused"
        :cart="cart"
        :mode="mode"
        :store="store"
        @update:focused="$emit('update:focused', $event)"
      />

      <!-- Category nav (hidden when searching) -->
      <CategoryNav
        :category="category"
        :search="search"
        :categories="store.categories.value"
        @update:category="$emit('update:category', $event)"
      />

      <!-- Product area -->
      <div class="pedido-products">
        <div v-if="filteredProducts.length === 0" class="pedido-empty">
          No hay productos que coincidan.
        </div>

        <TarjetasMode
          v-else-if="mode === 'tarjetas'"
          :products="filteredProducts"
          :focusedComedor="focused"
          :cart="cart"
          :store="store"
          @set-qty="handleSetQty"
        />

        <ListaDensaMode
          v-else-if="mode === 'lista'"
          :products="filteredProducts"
          :focusedComedor="focused"
          :cart="cart"
          :store="store"
          @set-qty="handleSetQty"
        />

        <MatrizMode
          v-else-if="mode === 'matriz'"
          :products="filteredProducts"
          :activeIds="activeIds"
          :cart="cart"
          :mutedIds="mutedIds"
          :search="search"
          :category="category"
          :store="store"
          @set-qty="handleSetQty"
          @toggle-muted="$emit('update:muted', $event)"
        />
      </div>
    </div>

    <!-- Right: order rail (excludes muted comedores) -->
    <OrderRail
      :activeIds="activeIds.filter(id => !mutedIds.includes(id))"
      :cart="cart"
      :notes="notes"
      :expanded="expanded"
      :store="store"
      @update:notes="$emit('update:notes', $event)"
      @update:expanded="$emit('update:expanded', $event)"
      @checkout="$emit('checkout')"
    />

  </div>
</template>

<script setup>
import { computed, watch } from 'vue'
import { getOrder }  from '../services/orders.js'
import QuickActions  from './QuickActions.vue'
import ComedorStrip  from './ComedorStrip.vue'
import CategoryNav   from './CategoryNav.vue'
import TarjetasMode  from './modes/TarjetasMode.vue'
import ListaDensaMode from './modes/ListaDensaMode.vue'
import MatrizMode    from './modes/MatrizMode.vue'
import OrderRail     from './OrderRail.vue'

const props = defineProps({
  activeIds: { type: Array,  required: true },
  cart:      { type: Object, required: true },
  notes:     { type: Object, required: true },
  search:    { type: String, default: '' },
  category:  { type: String, default: 'all' },
  mode:      { type: String, default: 'tarjetas' },
  focused:   { type: [String, Number], default: null },
  mutedIds:  { type: Array,  default: () => [] },
  expanded:  { type: Object, required: true },
  store:     { type: Object, required: true },
})

const emit = defineEmits([
  'update:cart', 'update:notes', 'update:search', 'update:category',
  'update:mode', 'update:focused', 'update:muted', 'update:expanded',
  'checkout',
])

// Refresh product catalog on every category switch so newly-added products appear
watch(() => props.category, () => {
  props.store.reloadProducts?.()
})

const filteredProducts = computed(() => {
  let list = props.store.products.value
  if (props.search) {
    const q = props.search.toLowerCase()
    list = list.filter(p =>
      p.name.toLowerCase().includes(q) ||
      (p.sku && p.sku.toLowerCase().includes(q))
    )
  } else if (props.category !== 'all') {
    list = list.filter(p => p.cat === props.category)
  }
  return list
})

function handleSetQty({ comedorId, productId, qty }) {
  const newCart = JSON.parse(JSON.stringify(props.cart))
  if (!newCart[comedorId]) newCart[comedorId] = {}
  if (qty > 0) {
    newCart[comedorId][productId] = qty
  } else {
    delete newCart[comedorId][productId]
  }
  emit('update:cart', newCart)
}

async function handleApplyLastOrder(comedorId) {
  const lo = props.store.lastOrders[comedorId]
  if (!lo?.orderId) return

  let lines = lo.items?.length ? lo.items : []
  if (!lines.length) {
    try {
      const res = await getOrder(lo.orderId)
      const full = res?.order || res || {}
      lines = full.order_details || full.details || []
    } catch { return }
  }
  if (!lines.length) return

  const newCart = JSON.parse(JSON.stringify(props.cart))
  if (!newCart[comedorId]) newCart[comedorId] = {}
  lines.forEach(line => {
    const productId = line.product?.id ?? line.product_id
    const qty       = line.quantity || 0
    if (productId && qty > 0) newCart[comedorId][productId] = qty
  })
  emit('update:cart', newCart)
}
</script>

<style scoped>
.pedido-layout {
  display: flex;
  flex: 1;
  min-height: 0;
}

.pedido-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  overflow: hidden;
}

.pedido-products {
  flex: 1;
  overflow-y: auto;
  background: var(--bg);
}

.pedido-empty {
  padding: 60px;
  text-align: center;
  color: var(--text-2);
  font-family: var(--ff-body);
}
</style>
